/**
 * Table tiles for the room board, and the drill-down of one table:
 * every seat's list with the hidden backs, the shared kit, interrupts,
 * prompts, traps and the morgue. Patients open a drawer with their journey
 * and Control's corrections.
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { FacultyPatient, Role, TableDetail, TableTile } from '@nightshift/engine/client';
import { api } from '../net/api.ts';
import { CertaintyTrack, ClockRow, Fold, Pips, ROLE_SHORT, ROLE_TITLE, Sheet, TriageBadge } from '../ui/bits.tsx';
import { FeedRow, type Ctl } from './SessionScreen.tsx';
import type { FeedItem, Timeline } from './wire.ts';

// ───────────────────────────── tile ─────────────────────────────

/** Display each seat's current census and acknowledged planning state. */
function TableSeats({ t, connected }: { t: TableTile; connected: Set<string> }): ReactElement {
  return <span className="tile-seats">
    {t.seats.map(s => <span key={s.role} className={`ts spec-${s.role}`} title={`${s.title}${s.name ? `, ${s.name}` : ''}; plan saved ${s.draftRevision} times`}>
      <span className="spec-dot" />
      <span className="ts-role">{ROLE_SHORT[s.role]}</span>
      {!s.bot && <i className={`conn ${connected.has(`${t.no}:${s.role}`) ? 'on' : ''}`} />}
      {s.bot && <span className="muted small">bot</span>}
      <span className="small">{s.ready ? 'Ready' : 'Not ready'} · {s.draftActions} planned</span>
      <span className="ts-pts small">{s.patients} patients</span>
    </span>)}
  </span>;
}

export function TableTileView({ t, connected, onOpen }: { t: TableTile; connected: string[]; onOpen: () => void }) {
  const on = new Set(connected);
  // What Control should look at first: dropped phones, then the table's own alerts.
  const offline = t.seats.filter((s) => !s.bot && !on.has(`${t.no}:${s.role}`)).map((s) => `${ROLE_TITLE[s.role]}'s phone is offline`);
  const attention = [...offline, ...t.alerts];
  return (
    <button className={`tile ${t.divert ? 'divert' : ''} ${attention.length ? 'attn' : ''}`} onClick={onOpen} data-testid={`tile-${t.no}`}>
      <span className="tile-head">
        <span className="tile-no display">Table {t.no}</span>
        <span className="tile-lead">
          Lead: <strong>{ROLE_TITLE[t.lead]}</strong>
        </span>
        {t.divert && <span className="tag bad">divert</span>}
        {attention.length > 0 && (
          <span className="tile-attn-n" aria-label={`${attention.length} thing${attention.length > 1 ? 's' : ''} to look at`}>
            {attention.length}
          </span>
        )}
      </span>
      {attention.length > 0 && (
        <span className="tile-alerts top" data-testid={`tile-attn-${t.no}`}>
          {attention.slice(0, 3).map((a) => (
            <span key={a}>{a}</span>
          ))}
          {attention.length > 3 && <span className="muted">and {attention.length - 3} more</span>}
        </span>
      )}
      <span className="tile-dash">
        <span>
          <b className="display">{t.dashboard.saved}</b> saved
        </span>
        <span>
          <b className="display">{t.dashboard.harmed}</b> harmed
        </span>
        <span className={t.dashboard.died ? 'died' : ''}>
          <b className="display">{t.dashboard.died}</b> died
        </span>
      </span>
      <span className="tile-pressure">
        <span className="tp-n">{t.seats.reduce((total, s) => total + s.patients, 0)} patients</span>
        <span>{t.seats.filter(s => s.ready).length}/{t.seats.length} seats ready</span>
        <span className="muted small">
          {t.actionsSpent} of {t.actionsAvailable} actions
        </span>
      </span>
      <TableSeats t={t} connected={on} />
      {(t.vents || t.blood !== null || t.staff !== null) && (
        <span className="tile-layers small muted">
          {t.staff !== null && <span>{t.staff} Staff</span>}
          {t.vents && (
            <span>
              {t.vents.free}/{t.vents.total} vents
            </span>
          )}
          {t.blood !== null && <span>{t.blood} blood</span>}
        </span>
      )}
    </button>
  );
}

// ───────────────────────────── drill-down ─────────────────────────────

export function TableDetailView({ d, tile, spoilers, ctl, onBack, feed, backsDefault = false }: { d: TableDetail; tile?: TableTile; spoilers: boolean; ctl: Ctl; onBack?: () => void; feed: FeedItem[]; backsDefault?: boolean }) {
  const [backs, setBacks] = useState(backsDefault);
  const dash = tile?.dashboard ?? d.board.dashboard;
  const [openId, setOpenId] = useState<string | null>(null);
  const all = [...d.seats.flatMap((s) => s.list), ...d.incoming];
  const open = all.find((p) => p.id === openId) ?? null;
  const b = d.board;
  return (
    <div className={`detail ${backs ? 'backs' : 'veiled'}`}>
      <DetailHeading d={d} backs={backs} spoilers={spoilers} count={all.length} onBack={onBack} onBacks={setBacks} />

      <DetailKit b={b} dash={dash} />

      <PendingPatients patients={d.incoming} onOpen={setOpenId} />

      <DetailSeats seats={d.seats} table={d.no} lead={b.lead} ctl={ctl} onOpen={setOpenId} />

      <div className="detail-extra">
        <DetailPrompts d={d} />
        <DetailHistory d={d} onOpen={setOpenId} />
      </div>

      <PatientDrawer p={open} table={d.no} ctl={ctl} onClose={() => setOpenId(null)} feed={feed} seats={d.seats.map((s) => s.role)} />
    </div>
  );
}

/** Preserve the table identity and card-back visibility controls. */
function DetailHeading({ d, backs, spoilers, count, onBack, onBacks }: { d: Pick<TableDetail, 'no' | 'deck'> & { board: Pick<TableDetail['board'], 'lead' | 'divert'> }; backs: boolean; spoilers: boolean; count: number; onBack?: () => void; onBacks: (value: boolean) => void }): ReactElement {
  const b = d.board;
  return (
    <header className="detail-head">
      {onBack && (
        <button className="btn btn-small" onClick={onBack} data-testid="detail-back">
          All tables
        </button>
      )}
      <h1 className="display">Table {d.no}</h1>
      <span className="muted">
        Lead {ROLE_TITLE[b.lead]}; {count} patients
        {b.divert ? ', on divert' : ''}; deck {d.deck.remaining} left
      </span>
      {spoilers && (
        <label className="row small backs-toggle">
          <input type="checkbox" checked={backs} onChange={(e) => onBacks(e.target.checked)} data-testid="show-backs" /> Show the backs of the cards
        </label>
      )}
    </header>
  );
}

/** Preserve Control's observed equipment and outcome summary. */
function DetailKit({ b, dash }: { b: TableDetail['board']; dash: Pick<TableTile['dashboard'], 'saved' | 'harmed' | 'died'> }): ReactElement {
  return (
    <section className="detail-kit" aria-label="Shared kit">
      {b.scanner.map((s) => (
        <span key={s.box} className={`dk ${s.closed ? 'closed' : s.usedBy ? 'used' : ''}`}>
          Scanner {s.box + 1}: {s.closed ? 'closed' : s.usedBy ? ROLE_SHORT[s.usedBy] : 'free'}
          {s.allocatedTo ? `, kept for ${ROLE_SHORT[s.allocatedTo]}` : ''}
        </span>
      ))}
      <span className={`dk ${b.theatre.closed ? 'closed' : b.theatre.used ? 'used' : ''}`}>
        Theatre: {b.theatre.closed ?? (b.theatre.used ? 'used' : b.theatre.booked ? (b.theatre.anaesthetic ? 'ready' : 'booked, no anaesthetic') : b.theatre.anaesthetic ? 'anaesthetic waiting' : 'free')}
      </span>
      {b.icu && <span className="dk">
        ICU {b.icu.occupied}/{b.icu.beds}
      </span>}
      {b.vents && (
        <span className="dk">
          Vents {b.vents.free}/{b.vents.total}
        </span>
      )}
      {b.blood && <span className="dk">Blood {b.blood.units}</span>}
      {b.locum && <span className="dk">Locum, {b.locum.actionsLeft} left</span>}
      <span className="dk">{b.callsLeft} calls left</span>
      <span className="dk">Time-Out {b.timeOutNow ? 'now' : b.timeOutUsed ? 'used' : 'unused'}</span>
      <span className="dk">
        {dash.saved} saved, {dash.harmed} harmed, {dash.died} died
      </span>
    </section>
  );
}

/** Preserve active seat cards and seat correction controls. */
function DetailSeats({ seats, table, lead, ctl, onOpen }: { seats: TableDetail['seats']; table: number; lead: Role; ctl: Ctl; onOpen: (id: string) => void }): ReactElement {
  return (
    <div className="seat-cols">
      {seats.map((s) => (
        <section key={s.role} className={`seat-col spec-${s.role}`} aria-label={s.title}>
          <header className="sc-head">
            <p className="sc-title">
              <span className="spec-dot" />
              <strong>{s.title}</strong>
              <span className="muted">{s.bot ? 'bot' : (s.name ?? '')}</span>
              {lead === s.role && <span className="lead-badge">Lead</span>}
            </p>
            <p className="sc-stats">
              <Pips available={s.actions.available} spent={s.actions.spent} />
              <span>{s.patients} patients · {s.draftActions} planned · {s.ready ? 'Ready' : 'Not ready'}</span>
            </p>
            {(s.huddleSentence || s.objective || !ctl.readonly) && (
              <Fold className="sc-more" title="Seat details" testId={`seat-details-${s.role}`}>
                {s.huddleSentence && <p className="sc-huddle">“{s.huddleSentence}”</p>}
                {s.objective && (
                  <p className="sc-obj hidden-bit">
                    <span className="muted">Objective:</span> {s.objective.title}
                  </p>
                )}
                {!ctl.readonly && <RestoreAction table={table} role={s.role} ctl={ctl} />}
              </Fold>
            )}
          </header>
          <div className="sc-list">
            {s.list.length === 0 && <p className="muted small">Nobody on the list.</p>}
            {s.list.map((p) => (
              <FacPatientCard key={p.id} p={p} onOpen={() => onOpen(p.id)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/** Preserve outstanding interruptions, choices and returning patients. */
function DetailPrompts({ d }: { d: Pick<TableDetail, 'interrupts' | 'prompts' | 'bounceSlot'> }): ReactElement {
  return <>
    {d.interrupts.length > 0 && (
      <section>
        <h3 className="sec-title">Interruptions</h3>
        <ul className="plain">
          {d.interrupts.map((i) => (
            <li key={i.id}>
              {ROLE_TITLE[i.seat]}: {i.title} <span className="muted">({i.state.toLowerCase()}, {i.age} turn{i.age === 1 ? '' : 's'})</span>
            </li>
          ))}
        </ul>
      </section>
    )}
    {d.prompts.length > 0 && (
      <section>
        <h3 className="sec-title">Waiting for an answer</h3>
        <ul className="plain">
          {d.prompts.map((q) => (
            <li key={q.id}>
              {ROLE_TITLE[q.seat]}: {q.kind === 'E17' ? 'comply or challenge the decree' : q.kind === 'E23' ? 'pass the Lead' : q.kind === 'E24' ? 'who covers the list' : q.kind}
            </li>
          ))}
        </ul>
      </section>
    )}
    {d.bounceSlot.length > 0 && (
      <section>
        <h3 className="sec-title">Coming back</h3>
        <ul className="plain">
          {d.bounceSlot.map((x) => (
            <li key={x.name}>
              {x.name}, turn {x.returnTurn}
            </li>
          ))}
        </ul>
      </section>
    )}
  </>;
}

/** Preserve spoiler-sensitive histories and recorded disputes. */
function DetailHistory({ d, onOpen }: { d: Pick<TableDetail, 'traps' | 'morgue' | 'disputes'>; onOpen: (id: string) => void }): ReactElement {
  return <>
    {d.traps.length > 0 && (
      <section className="hidden-bit">
        <h3 className="sec-title">Traps drawn</h3>
        <ul className="plain">
          {d.traps.map((x) => (
            <li key={x.id}>
              <button className="linkish" onClick={() => onOpen(x.id)}>
                {x.name}
              </button>
              : looks like {x.provisional}, is {x.truth}
              {x.bias.length ? <span className="muted"> ({x.bias[0]})</span> : null}
            </li>
          ))}
        </ul>
      </section>
    )}
    {d.morgue.length > 0 && (
      <section>
        <h3 className="sec-title">Died</h3>
        <ul className="plain">
          {d.morgue.map((x) => (
            <li key={x.id}>
              {x.name}
              {x.truth ? <span className="muted hidden-bit"> ({x.truth})</span> : null}
            </li>
          ))}
        </ul>
      </section>
    )}
    {d.disputes.length > 0 && (
      <section>
        <h3 className="sec-title">Marked disputed</h3>
        <ul className="plain">
          {d.disputes.map((x, i) => (
            <li key={i}>
              Turn {x.turn}: {x.note}
            </li>
          ))}
        </ul>
      </section>
    )}
  </>;
}

/** Keep accepted transfers selectable until Control releases their care. */
function PendingPatients({ patients, onOpen }: { patients: FacultyPatient[]; onOpen: (id: string) => void }): ReactElement | null {
  if (patients.length === 0) return null;
  return <section aria-label="Awaiting next turn">
    <h3 className="sec-title">Awaiting next turn</h3>
    <p className="muted small">Accepted transfers cannot be cared for until the next turn starts.</p>
    <div className="sc-list">
      {patients.map(patient => <div key={patient.id}>
        {patient.holder && <p className="small">{`Expected by ${ROLE_TITLE[patient.holder]}`}</p>}
        <FacPatientCard p={patient} onOpen={() => onOpen(patient.id)} />
      </div>)}
    </div>
  </section>;
}

function RestoreAction({ table, role, ctl }: { table: number; role: Role; ctl: Ctl }) {
  return (
    <button
      className="btn btn-quiet btn-small sc-restore"
      onClick={() => {
        const reason = window.prompt(`Give ${ROLE_TITLE[role]} one action back. Why? (logged)`);
        if (reason !== null) void ctl.send({ type: 'CORRECT_RESTORE_ACTION', table, role, n: 1, reason: reason.slice(0, 200) || 'Control' });
      }}
    >
      Give back an action
    </button>
  );
}

function FacPatientCard({ p, onOpen }: { p: FacultyPatient; onOpen: () => void }) {
  const h = p.hidden;
  const wrong = h && h.truth !== p.believed;
  return (
    <button className={`fpc ${p.colour}`} onClick={onOpen} data-testid={`fpatient-${p.id}`}>
      <span className="fpc-top">
        <TriageBadge colour={p.colour} size={22} />
        <strong className="fpc-name">{p.name}</strong>
        <span className="muted small">{p.where}</span>
      </span>
      <span className="fpc-dx">
        {p.believed}
        {p.box === 100 ? ' ✓' : ` ${p.box}%`}
        {p.needsStabilise && <span className="tag bad">unstable</span>}
      </span>
      <span className="fpc-meta">
        <ClockRow ticks={p.ticks} len={p.clockLen} color="var(--white)" />
        <span className="small muted">{p.route ? `${p.route.code}: ${p.route.steps.find((s) => s.state === 'next')?.label ?? 'done'}` : 'no route'}</span>
      </span>
      {h && (
        <span className={`fpc-back hidden-bit ${wrong ? 'wrong' : ''}`}>
          {wrong ? `Actually ${h.truth}` : 'Diagnosis right'}
          {h.complication !== 'none' ? `; complication on ${h.complication}` : ''}
          {h.early !== 'tolerates' ? `; ${h.early}` : ''}
          {h.bounceBack ? '; bounces back' : ''}
        </span>
      )}
    </button>
  );
}

// ───────────────────────────── patient drawer ─────────────────────────────

const LINE_NAMES: Record<string, string> = { ASSESS: 'Assess', OPINION_PHYS: 'Phys opinion', OPINION_PSYCH: 'Psych opinion', OPINION_SURG: 'Surg opinion', SCAN: 'Scan' };

function PatientDrawer({ p, table, ctl, onClose, feed, seats }: { p: FacultyPatient | null; table: number; ctl: Ctl; onClose: () => void; feed: FeedItem[]; seats: Role[] }) {
  const [journey, setJourney] = useState<FeedItem[] | null>(null);
  const [ticks, setTicks] = useState(0);
  const [moveTo, setMoveTo] = useState<Role>('EM');
  const [bed, setBed] = useState<string>('corridor');
  const id = p?.id;
  useEffect(() => {
    if (!id) return;
    setJourney(null);
    if (ctl.readonly) return;
    api<Timeline>(`/api/sessions/${ctl.sessionId}/timeline?table=${table}&min=0`)
      .then((t) => setJourney(t.events.filter((e) => e.patient === id && e.type !== 'ACTION_SPENT')))
      .catch(() => setJourney(null));
  }, [id, table, ctl.readonly, ctl.sessionId]);
  useEffect(() => {
    if (p) {
      setTicks(p.ticks);
      setMoveTo(p.holder ?? 'EM');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!p) return null;
  const h = p.hidden;
  const steps = journey ?? feed.filter((f) => f.patient === p.id);
  const reason = () => window.prompt('Why? (kept in the log)')?.slice(0, 200);
  return (
    <Sheet open={!!p} onClose={onClose} label={p.name}>
      <article className={`card-full ${p.colour}`}>
        <header className="cf-band">
          <TriageBadge colour={p.colour} size={32} />
          <div>
            <h2 className="cf-name">{p.name}</h2>
            <p className="cf-where">
              {p.holder ? ROLE_TITLE[p.holder] : ''}
              {p.where ? `, ${p.where}` : ''}
            </p>
          </div>
          <div className="cf-clock">
            <span className="cf-clock-n display">{p.clockLen - p.ticks}</span>
            <span className="cf-clock-l">boxes left</span>
          </div>
        </header>
        <section className="cf-prov">
          <span className="cf-label">{p.flipped ? 'Now known to be' : 'Provisional'}</span>
          <p className="cf-dx display">{p.believed}</p>
          {p.flipped && <p className="cf-was">Arrived as {p.provisional}</p>}
        </section>
        <section className="cf-track">
          <CertaintyTrack box={p.box} printed={p.printedBox} />
        </section>
        {h && (
          <section className="cf-lines hidden-bit">
            <span className="cf-label">The back of the card</span>
            <table>
              <tbody>
                <tr className={h.truth !== p.believed ? 'flip' : ''}>
                  <th>Truth</th>
                  <td>
                    {h.truth}
                    {h.trap ? ' (trap)' : ''}
                    {h.labelled ? ', labelled' : ''}
                    {h.barnacle ? ', Cranial Barnacles' : ''}
                  </td>
                </tr>
                {h.lines.map((l) => (
                  <tr key={l.test}>
                    <th>{LINE_NAMES[l.test] ?? l.test}</th>
                    <td>{l.text}</td>
                  </tr>
                ))}
                <tr>
                  <th>Complication</th>
                  <td>{h.complication}</td>
                </tr>
                <tr>
                  <th>Early</th>
                  <td>{h.early}</td>
                </tr>
                <tr>
                  <th>Bounce-back</th>
                  <td>{h.bounceBack ? 'yes' : 'no'}</td>
                </tr>
                {h.bias.length > 0 && (
                  <tr className="bias">
                    <th>Bias</th>
                    <td>
                      <strong>{h.bias[0]}.</strong> {h.bias[1]}
                    </td>
                  </tr>
                )}
                <tr>
                  <th>Card</th>
                  <td className="mono">{h.cardCode}</td>
                </tr>
              </tbody>
            </table>
          </section>
        )}
        {p.route && (
          <section className="cf-route">
            <span className="cf-label">{p.route.label}</span>
            <ol className="rail">
              {p.route.steps.map((s, i) => (
                <li key={i} className={s.state}>
                  <span>{s.label}</span>
                  <span className="rail-by">
                    {s.by}
                    {s.warn ? ', ⚠' : ''}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}
      </article>

      <section className="journey">
        <h3 className="sec-title">Journey</h3>
        <ol className="feed-list">
          {steps.map((f, i) => (
            <FeedRow key={`${f.seq}-${i}`} f={f} />
          ))}
          {steps.length === 0 && <li className="muted">Nothing recorded yet.</li>}
        </ol>
      </section>

      {!ctl.readonly && (
        <section className="corrections">
          <h3 className="sec-title">Correct a mistake at the table</h3>
          <div className="row wrap">
            <label className="row small">
              Clock
              <select className="input" value={ticks} onChange={(e) => setTicks(Number(e.target.value))}>
                {Array.from({ length: p.clockLen }, (_, i) => (
                  <option key={i} value={i}>
                    {i} of {p.clockLen}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="btn btn-small"
              disabled={ticks === p.ticks}
              onClick={() => {
                const r = reason();
                if (r !== undefined) void ctl.send({ type: 'CORRECT_CLOCK', p: p.id, ticks, reason: r || 'Control' });
              }}
            >
              Set the clock
            </button>
          </div>
          <div className="row wrap">
            <label className="row small">
              Move to
              <select className="input" value={moveTo} onChange={(e) => setMoveTo(e.target.value as Role)}>
                {seats.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_TITLE[r]}
                  </option>
                ))}
              </select>
            </label>
            <select className="input" value={bed} onChange={(e) => setBed(e.target.value)} aria-label="Bed">
              <option value="corridor">corridor</option>
              {Array.from({ length: 5 }, (_, i) => (
                <option key={i} value={String(i)}>
                  bed {i + 1}
                </option>
              ))}
            </select>
            <button
              className="btn btn-small"
              onClick={() => {
                const r = reason();
                if (r !== undefined) void ctl.send({ type: 'CORRECT_MOVE', p: p.id, role: moveTo, bed: bed === 'corridor' ? 'corridor' : Number(bed), reason: r || 'Control' });
              }}
            >
              Move
            </button>
          </div>
        </section>
      )}
    </Sheet>
  );
}
